import { Modal, Form, DatePicker, Select, InputNumber, Button, Checkbox, Space, Typography, Tooltip, Flex } from 'antd'
import Input from 'antd/es/input/Input'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import React, { useEffect, useMemo, useState } from 'react'

import { CreatorService } from '@services/CreatorService'
import { SchoolService } from '@services/SchoolService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { Creator, Product, School, Subscription } from '../../../../types'
import { subscriptionsStatuses, subscriptionTerms } from '../../../../types'

import { CancelSubscriptionModal } from './CancelSubscriptionModal'

dayjs.extend(weekday)
dayjs.extend(localeData)

interface EditSubscriptionModalProps {
    isOpen: boolean
    onClose: () => void
    products: Product[]
    seatCount: number
    subscription: Subscription | null // Replace 'any' with the actual Subscription type
    school: School | null
    refetch: () => Promise<void>
}

const availableStatuses = subscriptionsStatuses.filter(s => s !== 'CANCELLED')

export const EditSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({
    isOpen,
    onClose,
    school,
    products,
    seatCount,
    subscription,
    refetch
}) => {
    const confirm = useConfirmation()
    const [form] = Form.useForm()
    const [isLoading, setIsLoading] = useState(false)
    const [creators, setCreators] = useState<Creator[]>()
    const { showNotification } = useNotifications()
    const [selectedCreatorId, setSelectedCreatorId] = useState<string>()
    const [showCancelSubscription, setShowCancelSubscription] = useState<boolean>(false)

    useEffect(() => {
        const fetchCreators = async () => {
            const creators = await CreatorService.list()
            setCreators(creators)
        }
        fetchCreators()
    }, [])

    useEffect(() => {
        if (subscription && school) {
            form.setFieldsValue({
                name: subscription.name,
                status: subscription.status,
                term: subscription.term,
                dateRange: [dayjs(subscription.startDate), dayjs(subscription.endDate)],
                seatCount,
                creatorId: subscription.creatorId,
                productIds: subscription.subscriptionProducts.map((sp: any) => sp.product.id),
                isInternal: subscription.isInternal
            })
        }
    }, [subscription, form])

    useEffect(() => {
        if (subscription?.creatorId) {
            setSelectedCreatorId(subscription.creatorId)
        }
    }, [subscription])

    const handleSubmit = async (values: any) => {
        if (!school || !subscription) {
            return
        }

        const formattedValues = {
            name: values.name,
            startDate: values.dateRange[0].toISOString(),
            endDate: values.dateRange[1].toISOString(),
            schoolId: school.id,
            productIds: values.productIds || [],
            status: values.status,
            term: values.term,
            isInternal: values.isInternal,
            creatorId: values.creatorId
        }

        try {
            setIsLoading(true)
            await SchoolService.updateSubscription(school.id, subscription.id, formattedValues)
            onClose()
            await refetch()
        } catch (error) {
            showNotification({
                title: 'Error updating subscription',
                type: 'error'
            })
        } finally {
            setIsLoading(false)
        }
    }

    const onDeleteSubscription = async () => {
        if (!school || !subscription) {
            return
        }
        if (
            await confirm({
                title: 'Are you sure you want to delete this subscription?',
                confirmText: 'Delete'
            })
        ) {
            try {
                setIsLoading(true)
                await SchoolService.deleteSubscription(school.id!, subscription.id)
                onClose()
                await refetch()
            } catch (error) {
                showNotification({
                    title: 'Error deleting subscription',
                    type: 'error'
                })
            } finally {
                setIsLoading(false)
            }
        }
    }

    // No heavy restrictions on creator / product until we figure out our full subscription model
    const filteredProducts = useMemo(() => {
        const productIdsValue =
            form.getFieldValue('productIds') ?? subscription?.subscriptionProducts.map((sp: any) => sp.product.id) ?? []

        return products.filter(product => {
            if (productIdsValue && productIdsValue.includes(product.id)) {
                return true
            }

            if (selectedCreatorId) {
                return product.creatorId === selectedCreatorId
            }

            return false
        })
    }, [products, selectedCreatorId, subscription])

    if (!subscription) {
        return null
    }

    return (
        <Modal title="Edit Subscription" open={isOpen} onCancel={onClose} footer={null} width={700}>
            {subscription.stripeId && <Typography.Text>Stripe Subscriptions must be edited in Stripe </Typography.Text>}

            {!subscription.stripeId && (
                <Form form={form} onFinish={handleSubmit} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Name"
                        initialValue={subscription.name}
                        rules={[{ required: true, message: 'Please enter a subscription name' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="status"
                        label="Status"
                        initialValue={subscription.status}
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select>
                            {Object.values(availableStatuses).map(status => (
                                <Select.Option key={status} value={status}>
                                    {status}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="term"
                        label="Term"
                        initialValue={subscription.term}
                        rules={[{ required: true, message: 'Please select a term' }]}
                    >
                        <Select>
                            {Object.values(subscriptionTerms).map(term => (
                                <Select.Option key={term} value={term}>
                                    {term}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Internal Subscription (won't be billed)"
                        name="isInternal"
                        valuePropName="checked"
                        initialValue={subscription.isInternal}
                        layout="horizontal"
                    >
                        <Checkbox />
                    </Form.Item>

                    <Space size="large">
                        <Form.Item
                            name="dateRange"
                            label="Start & End Date"
                            initialValue={[dayjs(subscription.startDate), dayjs(subscription.endDate)]}
                            rules={[{ required: true, message: 'Please select a start and end date' }]}
                        >
                            <DatePicker.RangePicker />
                        </Form.Item>
                        <Form.Item label="Seat Count">
                            <Tooltip title="Use the seat change modal to edit seats">
                                <InputNumber disabled min={1} value={seatCount} />
                            </Tooltip>
                        </Form.Item>
                    </Space>

                    <Form.Item label="Creator" name="cre atorId" initialValue={subscription.creatorId}>
                        <Select
                            style={{ minWidth: '200px' }}
                            options={creators?.map(creator => ({
                                label: creator.name,
                                value: creator.id
                            }))}
                            onChange={creatorId => {
                                setSelectedCreatorId(creatorId)
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="productIds"
                        label="Products"
                        initialValue={subscription.subscriptionProducts.map(sp => sp.product.id)}
                    >
                        <Select mode="multiple">
                            {filteredProducts.map(product => (
                                <Select.Option key={product.id} value={product.id}>
                                    {product.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Flex justify="space-between">
                            <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
                                Update Subscription
                            </Button>
                            {subscription.status !== 'CANCELLED' && (
                                <>
                                    <Button
                                        type="dashed"
                                        danger
                                        onClick={() => setShowCancelSubscription(true)}
                                        disabled={!!subscription.stripeId}
                                    >
                                        Cancel Subscription
                                    </Button>
                                </>
                            )}
                            {subscription.status === 'CANCELLED' && (
                                <Button type="primary" danger onClick={onDeleteSubscription}>
                                    Delete Subscription
                                </Button>
                            )}
                        </Flex>
                    </Form.Item>
                </Form>
            )}
            {showCancelSubscription && school && (
                <CancelSubscriptionModal
                    schoolId={school.id}
                    subscription={subscription}
                    seatCount={seatCount}
                    isOpen={showCancelSubscription}
                    onClose={() => setShowCancelSubscription(false)}
                    onSuccess={() => onClose()}
                    refetch={refetch}
                />
            )}
        </Modal>
    )
}
